(() => {
  const vertexSource = `#version 300 es
    in vec2 position;
    void main() {
      gl_Position = vec4(position, 0.0, 1.0);
    }
  `;

  const fragmentSource = `#version 300 es
    precision highp float;
    uniform vec2 iResolution;
    uniform float iTime;
    uniform vec2 uMouse;
    out vec4 fragColor;

    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
    }

    void main() {
      float time = iTime * 0.35;
      vec2 p = 4.0 * ((gl_FragCoord.xy - 0.5 * iResolution.xy) / iResolution.y) - 0.5;
      p += (uMouse - 0.5) * 0.6;
      vec2 i = p;
      float c = 0.0;
      float r = length(p + vec2(sin(time), sin(time * 0.3 + 5.0)) * 0.5);
      float d = length(p);
      float rot = d + time + p.x;
      float cosRot = cos(rot);
      mat2 warp = mat2(cos(rot - sin(time / 5.0)), sin(rot), -sin(cosRot - time), cosRot) * -0.2;

      for (float n = 0.0; n < 3.0; n++) {
        p *= warp;
        float t = r - time / (n + 3.0);
        i -= p + vec2(cos(t - i.x - r) + sin(t + i.y), sin(t - i.y) + cos(t + i.x) + r);
        c += 0.16 / length(vec2(sin(i.x + t), cos(i.y + t)));
      }

      c /= 6.0;
      float intensity = max(c - 0.05, 0.0) * 1.3;
      float g = clamp(intensity, 0.0, 1.0);
      vec3 color1 = vec3(0.7451, 0.0706, 1.0);
      vec3 color2 = vec3(0.7529, 0.0, 1.0);
      vec3 color3 = vec3(1.0);
      vec3 col = mix(color1, color2, smoothstep(0.0, 0.5, g));
      col = mix(col, color3, smoothstep(0.5, 1.0, g));
      float grain = hash(gl_FragCoord.xy + iTime);
      float alpha = clamp(g + (grain - 0.5) * 0.05, 0.0, 1.0);
      fragColor = vec4(col * alpha, alpha);
    }
  `;

  const compileShader = (gl, type, source) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  };

  const setup = container => {
    if (container.dataset.moltenReady === 'true') return;
    container.dataset.moltenReady = 'true';

    const main = document.querySelector('#MainContent');
    if (main && container.parentElement !== main) main.prepend(container);

    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    const gl = canvas.getContext('webgl2', {
      alpha: true,
      antialias: false,
      premultipliedAlpha: true,
      powerPreference: 'high-performance'
    });

    if (!gl) {
      container.classList.add('sfx-molten-metal--fallback');
      return;
    }

    const vertex = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
    const fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
    if (!vertex || !fragment) {
      container.classList.add('sfx-molten-metal--fallback');
      return;
    }

    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      container.classList.add('sfx-molten-metal--fallback');
      return;
    }

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.useProgram(program);
    const position = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const resolution = gl.getUniformLocation(program, 'iResolution');
    const time = gl.getUniformLocation(program, 'iTime');
    const mouse = gl.getUniformLocation(program, 'uMouse');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;
    let visible = true;
    let pageVisible = !document.hidden;
    let startTime = performance.now();

    gl.clearColor(0, 0, 0, 0);
    container.appendChild(canvas);

    const resize = () => {
      const rect = container.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.6);
      const width = Math.max(1, Math.floor(rect.width * dpr));
      const height = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
        gl.uniform2f(resolution, width, height);
      }
    };

    const render = timestamp => {
      resize();
      gl.uniform1f(time, (timestamp - startTime) * 0.001);
      gl.uniform2f(mouse, 0.5, 0.5);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const loop = timestamp => {
      render(timestamp);
      frame = requestAnimationFrame(loop);
    };

    const stop = () => {
      if (!frame) return;
      cancelAnimationFrame(frame);
      frame = 0;
    };

    const start = () => {
      if (frame || !visible || !pageVisible || reducedMotion) return;
      startTime = performance.now() - 1200;
      frame = requestAnimationFrame(loop);
    };

    const onVisibility = () => {
      pageVisible = !document.hidden;
      if (pageVisible) start(); else stop();
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) start(); else stop();
    });

    observer.observe(container);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('resize', resize, { passive: true });
    resize();

    if (reducedMotion) render(1200); else start();

    container._moltenCleanup = () => {
      stop();
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', resize);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  };

  const boot = root => root.querySelectorAll('[data-sfx-molten]').forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-sfx-molten]').forEach(container => {
      if (container._moltenCleanup) container._moltenCleanup();
    });
  });
})();
